import { useDraggable } from '@dnd-kit/core';
import { Calendar, MessageSquare, Paperclip, User } from 'lucide-react';
import type { CustomField, Task } from '@/lib/schemas';
import { cn } from '@/lib/utils';
import { KanbanSubtaskPopover } from './kanban-subtask-popover';

// CSS.Translate.toString equivalent — dnd-kit ships it in
// @dnd-kit/utilities which is only a transitive dep here. We avoid
// pulling in another direct dep just for this 6-line helper.
function dragTransformToString(transform: { x: number; y: number; scaleX: number; scaleY: number } | null) {
    if (!transform) return undefined;
    const { x, y, scaleX, scaleY } = transform;
    const scale = scaleX !== 1 || scaleY !== 1 ? ` scale(${scaleX}, ${scaleY})` : '';
    return `translate3d(${x}px, ${y}px, 0)${scale}`;
}

// ============================================
// KANBAN CARD
// ============================================
//
// Renders a single draggable card. Custom-field chips show below the title
// when the active view's `visibleCardFields` includes them.
//
// t8 fix (issue #8a): the resting card now matches the Table row's flat
// chrome — border-top + bg-surface-hover/20 + hover bg-surface-hover/50 +
// rounded-md. No more rounded-xl box-with-white-border on a darker bg.
//
// t8 fix (issue #8b): the DragOverlay copy is explicitly opaque with
// shadow + ring so the drop preview can never bleed through; resting
// cards in the source column are hidden (opacity:0) via the standard
// dnd-kit pattern (the DragOverlay renders the visual card while the
// source card has opacity:0 so the user never sees two copies).
//
// `overlay` toggles the wrapper class used when this card is rendered
// inside <DragOverlay/> during a drag.

interface KanbanCardProps {
    task: Task;
    customFields: CustomField[];
    visibleCardFieldIds: string[];
    onClick?: (task: Task) => void;
    /** t15: invoked when the user clicks a subtask row in the
        subtask popover. The parent (KanbanView) wires this to the
        existing task-detail panel handler. */
    onSelectSubtask?: (task: Task) => void;
    disabled?: boolean;
    overlay?: boolean;
}

function getOptionLabel(field: CustomField, value: unknown): { label: string; color?: string } | null {
    if (value == null) return null;
    if (Array.isArray(value)) {
        const labels = value
            .map((id) => field.options.find((o) => o.id === id)?.label)
            .filter((l): l is string => Boolean(l));
        if (labels.length === 0) return null;
        return { label: labels.join(', ') };
    }
    if (typeof value === 'boolean') {
        return { label: value ? '✓' : '—' };
    }
    if (typeof value === 'string' || typeof value === 'number') {
        const opt = field.options.find((o) => o.id === value);
        return { label: opt?.label ?? String(value), color: opt?.color };
    }
    return null;
}

// Resting (non-overlay) card chrome — flat-row feel that matches the
// Table view's task rows (border-t border-border hover:bg-surface-hover/50
// bg-surface-hover/20 rounded-md).
const KANBAN_CARD_REST_CLASS =
    'group cursor-grab active:cursor-grabbing select-none border-t border-border px-3 py-2 hover:bg-surface-hover/50 transition-colors bg-surface-hover/20 rounded-md touch-none';

// Overlay copy chrome — explicit opaque surface + elevated ring/shadow
// so the drop preview can never bleed through (issue #8b).
const KANBAN_CARD_OVERLAY_CLASS =
    'pointer-events-none rounded-md border border-primary/30 bg-surface px-3 py-2 shadow-2xl ring-2 ring-primary/30';

export function KanbanCard({ task, customFields, visibleCardFieldIds, onClick, onSelectSubtask, disabled, overlay }: KanbanCardProps) {
    // useDraggable lets the card start a drag. The id encodes both the task
    // id and the column it currently lives in so onDragEnd can decide
    // whether a same-column drop should be treated as a no-op (drag of
    // something we already had) or a real move.
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: task.id,
        data: { type: 'card', taskId: task.id, fromColumn: task.status },
        disabled,
    });

    const style: React.CSSProperties = overlay
        ? {
              transform: dragTransformToString(transform),
              // Opaque surface so the column drop-preview never bleeds
              // through the floating card.
              backgroundColor: 'var(--color-surface, rgb(24, 24, 27))',
          }
        : {
              // Resting card hidden via opacity:0 during drag — the
              // DragOverlay paints the visible copy. This is the
              // canonical dnd-kit "don't render two cards" pattern.
              opacity: isDragging ? 0 : 1,
              transition: isDragging ? 'none' : undefined,
          };

    const chips = visibleCardFieldIds
        .map((id) => {
            const field = customFields.find((f) => f.id === id);
            if (!field) return null;
            const chip = getOptionLabel(field, task.customFieldValues?.[id]);
            if (!chip) return null;
            return { field, ...chip };
        })
        .filter((c): c is { field: CustomField; label: string; color?: string } => c !== null);

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...(overlay ? {} : attributes)}
            {...(overlay ? {} : listeners)}
            aria-disabled={overlay ? undefined : false}
            aria-roledescription={disabled ? undefined : attributes['aria-roledescription']}
            onKeyDown={(event) => { if(!disabled) {listeners?.onKeyDown?.(event); return;} if(event.target !== event.currentTarget) return; if(event.key === 'Enter' || event.key === ' ') {event.preventDefault();onClick?.(task);} }}
            onClick={(e) => {
                // Suppress click-after-drag: dnd-kit doesn't fire click while
                // dragging, but the parent column might still receive a click
                // event with a tiny offset when the drag ends.
                if (isDragging) return;
                e.stopPropagation();
                onClick?.(task);
            }}
            className={cn(overlay ? KANBAN_CARD_OVERLAY_CLASS : KANBAN_CARD_REST_CLASS, disabled && !overlay && 'cursor-pointer active:cursor-pointer touch-auto')}
            data-testid={`kanban-card-${task.id}`}
        >
            <div className="flex items-start justify-between gap-2">
                <h4 className="text-sm font-medium text-foreground leading-snug line-clamp-3 break-words flex-1">
                    {task.title}
                </h4>
            </div>

            {chips.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                    {chips.map((chip) => (
                        <span
                            key={chip.field.id}
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium text-white"
                            style={{ backgroundColor: chip.color || '#6366f1' }}
                            title={chip.field.name}
                        >
                            {chip.label}
                        </span>
                    ))}
                </div>
            )}

            <div className="mt-2 flex items-center justify-between text-[11px] text-foreground-muted">
                <div className="flex items-center gap-2">
                    {task.assignees.length > 0 && (
                        <span className="inline-flex items-center gap-1" title={task.assignees.map((a) => a.user.name).join(', ')}>
                            <User className="h-3 w-3" />
                            {task.assignees.length}
                        </span>
                    )}
                    {task.dueDate && (
                        <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(task.dueDate).toLocaleDateString()}
                        </span>
                    )}
                    {/* t15: subtask popover — clicks here must NOT open
                        the task-detail panel; the popover swallows the
                        pointer/click events so the parent's draggable
                        and onClick stay quiet. */}
                    <KanbanSubtaskPopover
                        taskId={task.id}
                        subtaskCount={task.subtaskCount}
                        onSelectSubtask={onSelectSubtask}
                    />
                </div>
                <div className="flex items-center gap-2">
                    {task.commentCount > 0 && (
                        <span className="inline-flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" />
                            {task.commentCount}
                        </span>
                    )}
                    {task.attachmentCount > 0 && (
                        <span className="inline-flex items-center gap-1">
                            <Paperclip className="h-3 w-3" />
                            {task.attachmentCount}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}
